import { api } from "./api";

export interface DataExport {
  user: any;
  addresses: any[];
  orders: any[];
  orderItems: any[];
  payments: any[];
  invoices: any[];
  chatConversations: any[];
  chatMessages: any[];
  contactMessages: any[];
  carts: any[];
  consents: any[];
  policyAcceptances: any[];
  metadata: {
    exportedAt: string;
    userId?: string;
    email: string;
    storeId?: string;
    thirdPartyReferences?: {
      stripeCustomerId?: string;
    };
  };
}

export interface DeletionRequest {
  id: string;
  status: "pending" | "processing" | "completed" | "failed";
  requestType: "soft_delete" | "hard_delete" | "anonymize";
  requestedAt: string;
  scheduledFor?: string;
  completedAt?: string;
  errorMessage?: string;
  retentionPeriodDays?: number;
}

export interface Consent {
  id: string;
  consentType: "account_creation" | "marketing_emails" | "cookies" | "privacy_policy" | "terms_of_service";
  granted: boolean;
  version: string;
  revokedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PolicyAcceptance {
  id: string;
  policyType: "privacy" | "terms" | "cookie";
  version: string;
  acceptedAt: string;
}

/**
 * GDPR API methods
 */
export const gdprApi = {
  /**
   * Export user data in JSON format
   */
  exportData: async (): Promise<DataExport> => {
    const response = await api.get("/gdpr/export", {
      responseType: "blob",
    });
    
    // Convert blob to JSON
    const text = await response.data.text();
    return JSON.parse(text);
  },

  /**
   * Export user data as downloadable file
   */
  exportDataAsFile: async (): Promise<void> => {
    const response = await api.get("/gdpr/export", {
      responseType: "blob",
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `gdpr-export-${Date.now()}.json`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Request data deletion
   */
  requestDeletion: async (params: {
    requestType: "soft_delete" | "hard_delete" | "anonymize";
    retentionPeriodDays?: number;
  }): Promise<{ requestId: string; status: string; scheduledFor?: string }> => {
    const response = await api.post("/gdpr/deletion-request", params);
    return response.data;
  },

  /**
   * Get deletion request status
   */
  getDeletionRequest: async (requestId: string): Promise<DeletionRequest> => {
    const response = await api.get(`/gdpr/deletion-request/${requestId}`);
    return response.data;
  },

  /**
   * Revoke consent
   */
  revokeConsent: async (consentType: string): Promise<void> => {
    await api.post("/gdpr/consent/revoke", { consentType });
  },
};

export interface StoreDataExport {
  store: any;
  storeAdmins: any[];
  products: any[];
  categories: any[];
  pages: any[];
  subscriptions: any[];
  storePlugins: any[];
  addresses: any[];
  chatConversations: any[];
  chatMessages: any[];
  contactMessages: any[];
  carts: any[];
  accountingSummary: {
    totalOrders: number;
    totalRevenue: number;
    totalPayments: number;
    totalInvoices: number;
    totalPayouts: number;
    note: string;
  };
  metadata: {
    exportedAt: string;
    storeId: string;
    thirdPartyReferences?: {
      stripeCustomerId?: string;
      stripeSubscriptionId?: string;
    };
  };
}

/**
 * Store GDPR API methods
 */
export const storeGdprApi = {
  /**
   * Export store data in JSON format
   */
  exportStoreData: async (): Promise<StoreDataExport> => {
    const response = await api.get("/gdpr/store/export", {
      responseType: "blob",
    });
    
    // Convert blob to JSON
    const text = await response.data.text();
    return JSON.parse(text);
  },

  /**
   * Export store data as downloadable file
   */
  exportStoreDataAsFile: async (): Promise<void> => {
    const response = await api.get("/gdpr/store/export", {
      responseType: "blob",
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `store-gdpr-export-${Date.now()}.json`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Request store deletion
   */
  requestStoreDeletion: async (params: {
    requestType: "soft_delete" | "hard_delete" | "anonymize";
    retentionPeriodDays?: number;
  }): Promise<{ requestId: string; status: string; scheduledFor?: string }> => {
    const response = await api.post("/gdpr/store/deletion-request", params);
    return response.data;
  },

  /**
   * Get store deletion request status
   */
  getStoreDeletionRequest: async (requestId: string): Promise<DeletionRequest> => {
    const response = await api.get(`/gdpr/store/deletion-request/${requestId}`);
    return response.data;
  },
};

/**
 * Consent API methods
 */
export const consentApi = {
  /**
   * Grant consent (public endpoint for cookie consent, authenticated for account consent)
   */
  grantConsent: async (params: {
    consentType: string;
    version?: string;
    email?: string;
  }): Promise<{ consent: Consent }> => {
    const response = await api.post("/consent/grant", params);
    return response.data;
  },

  /**
   * Get user's consents (authenticated only)
   */
  getMyConsents: async (): Promise<{ consents: Consent[] }> => {
    const response = await api.get("/consent/my-consents");
    return response.data;
  },

  /**
   * Revoke consent (authenticated only)
   */
  revokeConsent: async (consentType: string): Promise<void> => {
    await api.post("/consent/revoke", { consentType });
  },
};

/**
 * Policy API methods
 */
export const policyApi = {
  /**
   * Get current policy version (public endpoint)
   */
  getPolicyVersion: async (policyType: "privacy" | "terms"): Promise<{ policyType: string; version: string }> => {
    const response = await api.get(`/policies/${policyType}/version`);
    return response.data;
  },

  /**
   * Accept policy (authenticated or session-based)
   */
  acceptPolicy: async (params: {
    policyType: "privacy" | "terms" | "cookie";
    version: string;
    email?: string;
  }): Promise<{ acceptance: PolicyAcceptance }> => {
    const response = await api.post(`/policies/${params.policyType}/accept`, {
      version: params.version,
      email: params.email,
    });
    return response.data;
  },

  /**
   * Check policy acceptance status (authenticated only)
   */
  checkPolicyAcceptance: async (policyType: "privacy" | "terms" | "cookie"): Promise<{ policyType: string; isAccepted: boolean }> => {
    const response = await api.get(`/policies/${policyType}/acceptance`);
    return response.data;
  },

  /**
   * Get user's policy acceptances (authenticated only)
   */
  getMyPolicyAcceptances: async (): Promise<{ acceptances: PolicyAcceptance[] }> => {
    const response = await api.get("/policies/my-acceptances");
    return response.data;
  },
};

